import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressWorkflowApiV1WorkflowsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/workflow-server-web',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/workflow-server-web',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/workflow-server-web',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/workflows',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_workflows_workflows';

/**
 * Retrieves a list of the site's workflows info.
 *
 *
 * The `listWorkflows()` function returns a Promise that resolves to a list containing information about the site's workflows.
 *
 *
 * Use the `options` parameter to specify which workflows to retrieve and in which order to retrieve them. Workflows can be sorted based on their `"name"`.
 *
 *
 * If no `limit` parameter is passed, the first 100 workflows are returned. Sort order defaults to by `"name"` ascending.
 */
export function listWorkflows(payload: object): RequestOptionsFactory<any> {
  function __listWorkflows({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.workflow',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.WorkflowsService.ListWorkflows',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1WorkflowsServiceUrl({
        protoPath: '/v1/workflows',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'workflows.createdAt' },
              { path: 'workflows.lastAccess' },
              { path: 'workflows.createdDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveComWixpressWorkflowApiV1WorkflowsServiceUrl({
            protoPath: '/v1/workflows',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __listWorkflows;
}

/**
 * Retrieves a workflow by ID.
 *
 *
 * The `getWorkflow()` function returns a Promise that resolves to the workflow info with the specified ID.
 *
 *
 * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function getWorkflow(payload: object): RequestOptionsFactory<any> {
  function __getWorkflow({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.workflow',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.WorkflowsService.GetWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1WorkflowsServiceUrl({
        protoPath: '/v1/workflows/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'workflow.info.createdAt' },
              { path: 'workflow.info.lastAccess' },
              { path: 'workflow.info.createdDate' },
              { path: 'workflow.winPhase.cardsList.cards.info.dueDate' },
              { path: 'workflow.winPhase.cardsList.cards.info.createdAt' },
              { path: 'workflow.winPhase.cardsList.cards.info.updatedAt' },
              { path: 'workflow.winPhase.cardsList.cards.info.createdDate' },
              { path: 'workflow.winPhase.cardsList.cards.info.updatedDate' },
              {
                path: 'workflow.phasesList.phases.cardsList.cards.info.dueDate',
              },
              {
                path: 'workflow.phasesList.phases.cardsList.cards.info.createdAt',
              },
              {
                path: 'workflow.phasesList.phases.cardsList.cards.info.updatedAt',
              },
              {
                path: 'workflow.phasesList.phases.cardsList.cards.info.createdDate',
              },
              {
                path: 'workflow.phasesList.phases.cardsList.cards.info.updatedDate',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveComWixpressWorkflowApiV1WorkflowsServiceUrl({
            protoPath: '/v1/workflows/{id}',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __getWorkflow;
}

/**
 * Updates an existing workflow.
 *
 *
 * The `updateWorkflow()` function returns a Promise that resolves when the workflow has been updated with the specified values.
 *
 *
 * Only the properties passed in the `workflow` object will be updated. All other properties will remain the same.
 *
 *
 * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflow API, see [Retrieving IDs](#retrieving-ids).
 */
export function updateWorkflow(payload: object): RequestOptionsFactory<any> {
  function __updateWorkflow({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'workflow.createdAt' },
          { path: 'workflow.lastAccess' },
          { path: 'workflow.createdDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.workflows.v1.workflow',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.WorkflowsService.UpdateWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1WorkflowsServiceUrl({
        protoPath: '/v1/workflows/{id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __updateWorkflow;
}

/**
 * Creates a new workflow.
 *
 *
 * The `createWorkflow()` function returns a Promise that resolves to the created workflow ID.
 */
export function createWorkflow(payload: object): RequestOptionsFactory<any> {
  function __createWorkflow({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'workflow.createdAt' },
          { path: 'workflow.lastAccess' },
          { path: 'workflow.createdDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.workflows.v1.workflow',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.WorkflowsService.CreateWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1WorkflowsServiceUrl({
        protoPath: '/v1/workflows',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __createWorkflow;
}

/**
 * Deletes a workflow.
 *
 *
 * The `deleteWorkflow()` function returns a Promise when the specified workflow has been deleted.
 *
 *
 * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function deleteWorkflow(payload: object): RequestOptionsFactory<any> {
  function __deleteWorkflow({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.workflow',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.WorkflowsService.DeleteWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1WorkflowsServiceUrl({
        protoPath: '/v1/workflows/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteWorkflow;
}
